import { skipToken } from '@reduxjs/toolkit/query';
import { useTenant } from '@/hooks';
import {
  useGetCurrentUserQuery,
  useGetExternalProjectsQuery,
  useGetExternalPubliclyAssignedProjectsQuery,
} from '@/state';
import { useMemo, useState } from 'react';
import { CreationWizardManager } from '../creation-wizard/manager';
import { ProjectsListPanel } from '../projects-list-panel';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { mapExternalProject } from '@/lib/components-utils/projects.utils';
import type { ProjectResponse, PubliclyAssignedProjectApiResponse } from '@/types';

/**
 * External Project Component
 * @note External Project also re-used the internal project UI so we need to pass the projectType as EXTERNAL to the ProjectsListPanel
 * @returns React.JSX.Element
 */
type ExternalProjectProps = {
  assignedExternalProjects?: boolean;
};

export const ExternalProject = ({ assignedExternalProjects }: ExternalProjectProps): React.JSX.Element => {
  const { tenant } = useTenant();
  const [showCreateProject, setShowCreateProject] = useState(false);
  const { isCompany, currentOrg, currentOrgId, isSchoolAdmin, isTeacher, isStudent } = useClientPermissions();
  const isCompanyTenant = Boolean(
    currentOrg?.client?.schema_name &&
    currentOrg.client.schema_name === tenant &&
    isCompany
  );
  const isAdmin = isSchoolAdmin || isCompanyTenant;

  const {
    data: internalProjects = [],
    isLoading: isInternalLoading,
    isFetching: isInternalFetching,
    error: internalError,
    refetch: refetchInternal,
  } = useGetExternalProjectsQuery(
    tenant && isAdmin
      ? {
          tenant,
          isCompany: isCompanyTenant,
          assignedSchools: currentOrgId ? String(currentOrgId) : undefined,
        }
      : skipToken
  );

  const { data: publicAssignments = [], isLoading: isPublicLoading, isFetching: isPublicFetching, error: publicError, refetch: refetchPublic } =
    useGetExternalPubliclyAssignedProjectsQuery({}, { skip: !tenant });

  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUserQuery(
    tenant ? { tenant } : skipToken
  );

  const publicProjects = useMemo(() => {
    const firstByProject = new Map<number, PubliclyAssignedProjectApiResponse>();
    publicAssignments.forEach((assignment) => {
      // project_ref.id can identify different reference rows for the same
      // underlying project; project_ref.project_id is the stable project key.
      const projectId = assignment.project_ref?.project_id ?? assignment.project_ref?.id;
      if (projectId && !firstByProject.has(projectId)) {
        firstByProject.set(projectId, assignment);
      }
    });

    return Array.from(firstByProject.values()).map((assignment) => {
      const projectId = assignment.project_ref?.id ?? assignment.project_ref?.project_id!;
      return mapExternalProject({
        ...assignment,
        ...(assignment.project_ref ?? {}),
        id: projectId,
        project_id: projectId,
        project_resorces_id: assignment.id,
        project_ref: assignment.project_ref,
      });
    });
  }, [publicAssignments]);

  const visibleProjects = useMemo<ProjectResponse[]>(() => {
    if (!isAdmin) {
      if (!isTeacher && !isStudent) return [];
      const userId = currentUser?.id;
      return publicProjects.filter((project) =>
        project.public_project_candidates?.some((candidate) => candidate.candidate?.id === userId) ||
        project.members?.some((member: any) => member?.id === userId)
      );
    }

    const projects = internalProjects.map((project) => {
      const assigned = publicProjects.find((publicProject) =>
        publicProject.id === project.id ||
        publicProject.project_id === project.id ||
        publicProject.project_ref?.project_id === project.id ||
        publicProject.project_ref?.id === project.project_id
      );
      return assigned
        ? {
            ...project,
            project_resorces_id: assigned.project_resorces_id,
            publiclyAssigned: true,
          }
        : project;
    });

    if (assignedExternalProjects === undefined) return projects;
    return projects.filter((project) =>
      assignedExternalProjects ? project.publiclyAssigned : !project.publiclyAssigned
    );
  }, [assignedExternalProjects, currentUser?.id, internalProjects, isAdmin, isStudent, isTeacher, publicProjects]);

  const isLoading = (isAdmin ? isInternalLoading : isPublicLoading) ||
    (!isAdmin && isCurrentUserLoading);
  const isFetching = isInternalFetching || isPublicFetching;
  const error = isAdmin ? internalError || publicError : publicError;
  const refetch = async (): Promise<{ data?: unknown; error?: unknown }> => {
    if (isAdmin) {
      await refetchInternal();
    }
    const result = await refetchPublic();
    return { data: result.data, error: result.error };
  };

  return (
    <>
      <CreationWizardManager
        open={showCreateProject}
        onOpenChange={setShowCreateProject}
        projectType="EXTERNAL"
      />
      <ProjectsListPanel
        error={error}
        title={assignedExternalProjects === true ? 'Assigned External Projects' : assignedExternalProjects === false ? 'Not Assigned External Projects' : 'External Projects'}
        projects={visibleProjects}
        isLoading={isLoading}
        isFetching={isFetching}
        onRetry={refetch}
        createLabel="Create External Project"
        projectType="EXTERNAL"
        assignedExternalProjects={assignedExternalProjects}
        onCreate={
          isCompanyTenant ? () => setShowCreateProject(true) : undefined
        }
      />
    </>
  );
};
